/**
 * eBay Listing Generator
 * Generates a ready-to-paste eBay listing from a grade/condition result.
 */

(function (window) {
    'use strict';

    const CONDITION_MAP = {
        'Gem Mint': 3000,      // PSA 10 equivalent
        'Mint': 2750,          // PSA 9
        'Near Mint': 2000,     // PSA 8
        'Excellent': 1500,     // PSA 7
        'Very Good': 1000,     // PSA 6
        'Good': 700,           // PSA 5
        'Fair': 500,           // PSA 4
        'Poor': 400,           // PSA 3
        'Damaged': 300         // PSA 2 or below
    };

    const EbayListingGenerator = {
        init() {
            this.createModal();
        },

        createModal() {
            if (document.getElementById('ebay-listing-modal')) return;

            const modal = document.createElement('div');
            modal.id = 'ebay-listing-modal';
            modal.style.cssText = `
                display: none;
                position: fixed;
                inset: 0;
                background: rgba(0,0,0,0.6);
                backdrop-filter: blur(4px);
                z-index: 999999;
                align-items: center;
                justify-content: center;
                padding: 20px;
                overflow-y: auto;
            `;
            modal.innerHTML = `
                <div data-flex-nowrap style="background: white; border-radius: var(--radius-2xl); max-width: 760px; width: 100%; min-width: 0; max-height: 90vh; max-height: 90dvh; display: flex; flex-direction: column; overflow: hidden; box-shadow: 0 25px 50px rgba(0,0,0,0.25);"><!-- data-flex-nowrap: the mobile override in overrides.css wraps inline flex containers into columns; a modal card must stay column -->
                    <div style="padding: var(--spacing-6); border-bottom: 1px solid var(--color-border); display: flex; justify-content: space-between; align-items: center; flex: 0 0 auto;">
                        <h3 style="margin: 0; font-size: var(--font-size-xl);">eBay Listing Generator</h3>
                        <button type="button" id="ebay-modal-close" style="background: none; border: none; font-size: 24px; cursor: pointer; color: var(--color-text-muted);">&times;</button>
                    </div>
                    <div id="ebay-listing-content" style="padding: var(--spacing-6); flex: 1 1 auto; min-height: 0; overflow-y: auto; -webkit-overflow-scrolling: touch;">
                        <!-- Content injected here -->
                    </div>
                    <div id="ebay-listing-footer" style="padding: var(--spacing-4) var(--spacing-6) calc(var(--spacing-4) + env(safe-area-inset-bottom)); border-top: 1px solid var(--color-border); text-align: center; flex: 0 0 auto; background: #fff;">
                        <!-- Actions injected here — pinned so they're never below the fold -->
                    </div>
                </div>
            `;
            document.body.appendChild(modal);

            modal.addEventListener('click', (e) => {
                if (e.target === modal) this.close();
            });
            document.getElementById('ebay-modal-close').addEventListener('click', () => this.close());
        },

        open(record, fallbackName, images = {}) {
            this.createModal();
            const modal = document.getElementById('ebay-listing-modal');
            const content = document.getElementById('ebay-listing-content');
            const listing = this.generate(record, fallbackName, images);

            content.innerHTML = `
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr)); gap: var(--spacing-6); margin-bottom: var(--spacing-6);">
                    <div>
                        <label style="font-size: var(--font-size-xs); font-weight: 700; text-transform: uppercase; color: var(--color-text-muted); display: block; margin-bottom: var(--spacing-2);">eBay Title</label>
                        <div id="ebay-title" style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: var(--radius-lg); padding: var(--spacing-3); font-size: var(--font-size-sm); line-height: 1.5; min-height: 60px;">${this.escapeHtml(listing.title)}</div>
                        <div style="display: flex; justify-content: space-between; margin-top: var(--spacing-2);">
                            <span style="font-size: var(--font-size-xs); color: var(--color-text-muted);">${listing.title.length}/80 chars</span>
                            <button type="button" class="copy-btn" data-target="ebay-title" style="font-size: var(--font-size-xs); background: var(--color-primary); color: white; border: none; padding: 4px 10px; border-radius: var(--radius-md); cursor: pointer;">Copy</button>
                        </div>
                    </div>
                    <div>
                        <label style="font-size: var(--font-size-xs); font-weight: 700; text-transform: uppercase; color: var(--color-text-muted); display: block; margin-bottom: var(--spacing-2);">Suggested Price</label>
                        <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: var(--radius-lg); padding: var(--spacing-4); text-align: center;">
                            <div style="font-size: var(--font-size-3xl); font-weight: 800; color: #166534;">${listing.price}</div>
                            <div style="font-size: var(--font-size-xs); color: #64748b; margin-top: var(--spacing-1);">Based on predicted grade and market data</div>
                        </div>
                    </div>
                </div>

                <div style="margin-bottom: var(--spacing-6);">
                    <label style="font-size: var(--font-size-xs); font-weight: 700; text-transform: uppercase; color: var(--color-text-muted); display: block; margin-bottom: var(--spacing-2);">eBay Item Specifics</label>
                    <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: var(--spacing-3); background: #f8fafc; border: 1px solid #e2e8f0; border-radius: var(--radius-lg); padding: var(--spacing-4); font-size: var(--font-size-sm);">
                        <div><strong>Condition:</strong> ${listing.ebayCondition}</div>
                        <div><strong>Grader:</strong> Pre-Grade AI Estimate</div>
                        <div><strong>Grade:</strong> ${listing.grade}</div>
                        <div><strong>Card Name:</strong> ${listing.cardName}</div>
                    </div>
                </div>

                <div style="margin-bottom: var(--spacing-6);">
                    <label style="font-size: var(--font-size-xs); font-weight: 700; text-transform: uppercase; color: var(--color-text-muted); display: block; margin-bottom: var(--spacing-2);">Description</label>
                    <textarea id="ebay-description" readonly style="width: 100%; min-height: 220px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: var(--radius-lg); padding: var(--spacing-4); font-size: var(--font-size-sm); line-height: 1.6; font-family: inherit; resize: vertical;">${this.escapeHtml(listing.description)}</textarea>
                    <div style="display: flex; justify-content: flex-end; margin-top: var(--spacing-2);">
                        <button type="button" class="copy-btn" data-target="ebay-description" style="font-size: var(--font-size-xs); background: var(--color-primary); color: white; border: none; padding: 4px 10px; border-radius: var(--radius-md); cursor: pointer;">Copy Description</button>
                    </div>
                </div>

                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr)); gap: var(--spacing-6); align-items: start;">
                    <div>
                        <label style="font-size: var(--font-size-xs); font-weight: 700; text-transform: uppercase; color: var(--color-text-muted); display: block; margin-bottom: var(--spacing-2);">Listing Photo</label>
                        ${listing.imageUrl ? `
                        <img src="${listing.imageUrl}" alt="${this.escapeHtml(listing.cardName)}" style="width: 100%; max-height: 220px; object-fit: contain; border-radius: var(--radius-lg); border: 1px solid #e2e8f0; background: #f8fafc;">
                        ` : `
                        <div style="width: 100%; height: 180px; background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: var(--radius-lg); display: flex; align-items: center; justify-content: center; text-align: center; padding: var(--spacing-4); color: #64748b; font-size: var(--font-size-sm);">
                            No card photo available.
                        </div>
                        `}
                        ${listing.isRawScan ? `
                        <div style="background: #fffbeb; border: 1px solid #fcd34d; border-radius: var(--radius-lg); padding: var(--spacing-3); margin-top: var(--spacing-3); font-size: var(--font-size-xs); color: #92400e; line-height: 1.5;">
                            <strong>Tip:</strong> This is your raw scan. For eBay, use a cropped/card-only image or take a clean, well-lit photo on a neutral background.
                        </div>
                        ` : `
                        <p style="font-size: var(--font-size-xs); color: var(--color-text-muted); margin-top: var(--spacing-2);">Right-click and save to use as your primary photo.</p>
                        `}
                    </div>
                    <div>
                        <label style="font-size: var(--font-size-xs); font-weight: 700; text-transform: uppercase; color: var(--color-text-muted); display: block; margin-bottom: var(--spacing-2);">Quick Tips</label>
                        <ul style="font-size: var(--font-size-sm); color: var(--color-text-light); line-height: 1.6; padding-left: var(--spacing-6);">
                            <li>Use the title as-is for best search visibility.</li>
                            <li>Adjust price based on recent sold comps.</li>
                            <li>Add photos of the back and any flaws.</li>
                            <li>Mention this is a raw card with an AI pre-grade estimate.</li>
                        </ul>
                    </div>
                </div>

            `;

            // Pinned footer actions — previously at content bottom, unreachable
            // without scrolling on every viewport (Playwright audit).
            modal.querySelector('#ebay-listing-footer').innerHTML = `
                <a href="${listing.ebaySearchUrl}" target="_blank" rel="noopener" class="btn btn-outline" style="margin-right: var(--spacing-3);">View eBay Comps</a>
                <button type="button" class="btn btn-primary open-ebay-listing" data-url="${listing.ebaySellUrl}" data-description="${this.escapeHtml(listing.description)}" style="border: none;">List on eBay</button>
                <p style="margin: var(--spacing-2) 0 0; font-size: var(--font-size-xs); color: var(--color-text-muted);">
                    eBay's listing page will open with the title pre-filled. The description is copied to your clipboard so you can paste it into eBay.
                </p>
            `;

            modal.style.display = 'flex';
            document.body.style.overflow = 'hidden';

            content.querySelectorAll('.copy-btn').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const targetId = e.target.dataset.target;
                    const el = document.getElementById(targetId);
                    const text = el ? (el.value || el.textContent) : '';
                    navigator.clipboard.writeText(text.trim()).then(() => {
                        e.target.textContent = 'Copied!';
                        setTimeout(() => e.target.textContent = 'Copy', 1500);
                    });
                });
            });

            const ebayBtn = modal.querySelector('.open-ebay-listing');
            if (ebayBtn) {
                ebayBtn.addEventListener('click', (e) => {
                    const description = e.target.dataset.description || '';
                    const url = e.target.dataset.url || 'https://www.ebay.com/sl/list';
                    if (description) {
                        navigator.clipboard.writeText(description).then(() => {
                            e.target.textContent = 'Description copied — opening eBay...';
                            setTimeout(() => { e.target.textContent = 'List on eBay'; }, 2000);
                        });
                    }
                    window.open(url, '_blank', 'noopener,noreferrer');
                });
            }
        },

        close() {
            const modal = document.getElementById('ebay-listing-modal');
            if (modal) {
                modal.style.display = 'none';
                document.body.style.overflow = '';
            }
        },

        generate(record, fallbackName, images = {}) {
            const grades = record.grades || {};
            const conditionObj = record.condition || {};
            const finalGrade = grades.final || record.final_grade || conditionObj.value || 'N/A';
            const condition = grades.condition || record.final_grade_label || conditionObj.label || 'Grade';

            const identification = (record.sport_id != null ? record.sport_id.identification : void 0) || record.identification || (record.card != null ? record.card.identification : void 0) || {};
            const cardName = identification.full_name || identification.name || fallbackName || 'Sports Card';
            const year = identification.year || '';
            const setName = identification.set_name || '';
            const cardNumber = identification.card_number || '';
            const company = identification.company || '';
            const subcategory = identification.subcategory || '';
            const links = identification.links || {};

            let title = `${cardName}`.trim();
            if (year) title = `${year} ${title}`;
            if (setName) title = `${title} ${setName}`;
            if (cardNumber) title = `${title} #${cardNumber}`;
            if (finalGrade !== 'N/A') title = `${title} Pre-Grade ${finalGrade}`;
            if (title.length > 80) title = title.substring(0, 77) + '...';

            const ebayCondition = this.getEbayCondition(condition);
            const ebayConditionName = this.getEbayConditionName(ebayCondition);
            const price = this.suggestPrice(record, finalGrade);

            const searchQuery = encodeURIComponent(`${cardName} ${year} ${setName}`.trim());
            const ebaySearchUrl = `https://www.ebay.com/sch/i.html?_nkw=${searchQuery}&LH_Sold=1&LH_Complete=1`;
            // eBay's identify page pre-fills the title for the listing flow.
            const ebaySellUrl = `https://www.ebay.com/sl/prelist/identify?title=${encodeURIComponent(title)}`;

            // Prefer locally-saved _images paths; Ximilar-hosted URLs expire quickly.
            const imageUrl = images.cropped || images.exact || images.clean || images.full ||
                (record._images != null ? record._images.cropped : void 0) || (record._images != null ? record._images.exact : void 0) || (record._images != null ? record._images.clean : void 0) || (record._images != null ? record._images.full : void 0) ||
                record._exact_url_card || record._full_url_card || record._clean_url_card ||
                record._cropped_url_card || record._img_url ||
                record.original_image_url || images.original || '';
            const isRawScan = !!(images.original || record.original_image_url) &&
                !images.cropped && !images.exact && !images.clean && !images.full &&
                !(record._images != null ? record._images.cropped : void 0) && !(record._images != null ? record._images.exact : void 0) && !(record._images != null ? record._images.clean : void 0) && !(record._images != null ? record._images.full : void 0) &&
                !record._exact_url_card && !record._full_url_card && !record._clean_url_card &&
                !record._cropped_url_card && !record._img_url;

            const description = this.buildDescription({
                cardName,
                year,
                setName,
                cardNumber,
                company,
                subcategory,
                grade: finalGrade,
                condition,
                ebayConditionName,
                subgrades: grades,
                price
            });

            return {
                title,
                cardName,
                grade: finalGrade,
                condition,
                ebayCondition,
                ebayConditionName,
                price,
                description,
                imageUrl,
                isRawScan,
                ebaySearchUrl,
                ebaySellUrl
            };
        },

        getEbayCondition(conditionLabel) {
            const label = String(conditionLabel).toLowerCase();
            if (label.includes('gem') || label.includes('mint')) return 3000;
            if (label.includes('near')) return 2000;
            if (label.includes('excellent')) return 1500;
            if (label.includes('very good')) return 1000;
            if (label.includes('good')) return 700;
            if (label.includes('fair')) return 500;
            if (label.includes('poor')) return 400;
            return 3000;
        },

        getEbayConditionName(conditionId) {
            const map = {
                3000: 'Graded',
                2750: 'Graded',
                2000: 'Near Mint',
                1500: 'Excellent',
                1000: 'Very Good',
                700: 'Good',
                500: 'Fair',
                400: 'Poor',
                300: 'Poor'
            };
            return map[conditionId] || 'Used';
        },

        suggestPrice(record, grade) {
            const priceStats = (record.sport_id != null ? record.sport_id.price_stats : void 0) || (function(_o){return _o == null ? void 0 : _o.price_stats})((record.tcg_id != null ? record.tcg_id.card : void 0)) || record.price_stats || {};

            // price_stats can be either a flat object {average, median, mean} or
            // an array of {stats_type, value: {median, mean, latest, ...}} objects.
            let base = 0;
            if (Array.isArray(priceStats)) {
                // Find the ungraded stats first (best for pricing a raw card),
                // then fall back to overall, then graded.
                const priority = ['ungraded', 'overall', 'graded'];
                for (const preferred of priority) {
                    const panel = priceStats.find(p => (p.stats_type || '') === preferred);
                    if (panel && panel.value) {
                        base = parseFloat(panel.value.median || panel.value.mean || panel.value.latest || 0);
                        if (base > 0) break;
                    }
                }
            } else if (typeof priceStats === 'object' && priceStats !== null) {
                base = parseFloat(priceStats.average || priceStats.median || priceStats.mean || 0);
            }

            if (base <= 0) {
                const gradeNum = parseFloat(grade);
                if (!isNaN(gradeNum)) {
                    base = gradeNum >= 9 ? 25 : gradeNum >= 7 ? 12 : 5;
                } else {
                    base = 10;
                }
            }
            return '$' + base.toFixed(2);
        },

        buildDescription(data) {
            const subgrades = data.subgrades || {};
            return `Up for sale is a ${data.year ? data.year + ' ' : ''}${data.setName ? data.setName + ' ' : ''}${data.cardName}.

This card has been evaluated by PreGradeCards AI to provide a pre-submission grade estimate. This is NOT an official graded slab. The predicted grade is for informational purposes only.

**Predicted Grade:** ${data.grade} (${data.condition})

**Sub-Grade Breakdown:**
- Corners: ${subgrades.corners || 'N/A'}
- Edges: ${subgrades.edges || 'N/A'}
- Surface: ${subgrades.surface || 'N/A'}
- Centering: ${subgrades.centering || 'N/A'}

**Condition:** ${data.ebayConditionName}

**Suggested Listing Price:** ${data.price}

Please review the photos carefully. Additional photos available upon request. The card ships in a penny sleeve, top loader, and bubble mailer to ensure it arrives safely.

Thanks for looking!`;
        },

        escapeHtml(text) {
            const div = document.createElement('div');
            div.textContent = text;
            return div.innerHTML;
        }
    };

    window.EbayListingGenerator = EbayListingGenerator;
    document.addEventListener('DOMContentLoaded', () => EbayListingGenerator.init());
})(window);
